import type { ComponentChildren, JSX } from 'preact';
import { useState } from 'preact/hooks';
import { isAssistant, openTaskFile, splitInstructions, type KnownTaskFile, type TaskFileKind } from '../lib/taskFileOpen';

interface Props {
  /** Omit when the file cannot be served by id: the plain Drive link is used. */
  kind?: TaskFileKind | null;
  id?: number | string | null;
  href: string;
  name?: string | null;
  class?: string;
  testId?: string;
  ariaLabel?: string;
  title?: string;
  rel?: string;
  onClick?: (e: MouseEvent) => void;
  children: ComponentChildren;
}

/**
 * A task file link. Everyone but an assistant keeps the plain Google Drive
 * link. An assistant has no Google account, so the link opens the file through
 * the Worker (blob URL), and a Print button sits beside it.
 */
export function TaskFileLink(p: Props): JSX.Element {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { kind, id } = p;
  if (!isAssistant() || !kind || id === null || id === undefined) {
    return (
      <a
        class={p.class}
        href={p.href}
        target="_blank"
        rel={p.rel ?? 'noreferrer'}
        data-testid={p.testId}
        aria-label={p.ariaLabel}
        title={p.title}
        onClick={p.onClick}
      >
        {p.children}
      </a>
    );
  }
  async function go(print: boolean, e: MouseEvent): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    p.onClick?.(e);
    setMsg(null);
    setBusy(true);
    try {
      const out = await openTaskFile(kind as TaskFileKind, id as number | string, { print, name: p.name || undefined });
      if (out === 'downloaded') {
        setMsg('This kind of file cannot be shown here, so it was downloaded. Open it from your downloads to read or print it.');
      }
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'could not open that file');
    } finally {
      setBusy(false);
    }
  }
  return (
    <span class="task-file-proxy">
      <a
        class={p.class}
        href={p.href}
        rel="noreferrer"
        data-testid={p.testId}
        data-proxy-path={`/api/v1/task-files/${kind}/${id}/content`}
        aria-label={p.ariaLabel}
        title={p.title}
        onClick={(e) => { void go(false, e as MouseEvent); }}
      >
        {p.children}
      </a>
      {' '}
      <button
        type="button"
        class="link-button"
        disabled={busy}
        data-testid={`task-file-print-${kind}-${id}`}
        data-proxy-path={`/api/v1/task-files/${kind}/${id}/content`}
        onClick={(e) => { void go(true, e as MouseEvent); }}
      >
        Print
      </button>
      {msg ? <small class="muted" role="status" data-testid={`task-file-msg-${kind}-${id}`}> {msg}</small> : null}
    </span>
  );
}

/** Instruction text: for an assistant, this task's own Drive links open through the Worker. */
export function InstructionsText({ text, known, testId }: {
  text: string; known: KnownTaskFile[]; testId?: string;
}): JSX.Element {
  const parts = isAssistant() && known.length ? splitInstructions(text, known) : [text];
  return (
    <p class="instructions-text" data-testid={testId}>
      {parts.map((part, i) => (typeof part === 'string'
        ? part
        : (
          <TaskFileLink key={i} kind={part.file.kind} id={part.file.id} name={part.file.name} href={part.text}>
            {part.text}
          </TaskFileLink>
        )))}
    </p>
  );
}
